import React, { useCallback } from "react";
import { Block, DesignSystem, Plugin } from "../../types/ThemeTypes";
import { BaseBlockProps } from "../../types/BlockTypes";
import { BLOCK_COMPONENTS } from "../../constants/blockComponents";

interface HeaderProps {
  blocks: Block[];
  designSystem: DesignSystem;
  plugins: Plugin[];
  tenantDomain?: string;
  apiVersion?: string;
}

// Type-safe block component interface (simplified, no CMS props)
interface BlockComponentProps extends BaseBlockProps {
  designSystem?: DesignSystem;
  plugins?: Plugin[];
  tenantDomain?: string;
  apiVersion?: string;
}

// Type-safe block component factory with memoization
const createTypeSafeBlockComponent = (() => {
  const componentCache = new Map<
    string,
    React.ComponentType<BlockComponentProps>
  >();

  return (block: Block) => {
    const BlockComponent =
      BLOCK_COMPONENTS[block.key as keyof typeof BLOCK_COMPONENTS];

    if (!BlockComponent) return null;

    const cacheKey = block.key;

    if (componentCache.has(cacheKey)) {
      return componentCache.get(cacheKey)!;
    }

    const TypeSafeBlockComponent = React.memo((props: BlockComponentProps) => {
      return (
        <BlockComponent {...(props as unknown as Record<string, unknown>)} />
      );
    });

    componentCache.set(cacheKey, TypeSafeBlockComponent);
    return TypeSafeBlockComponent;
  };
})();

const Header: React.FC<HeaderProps> = ({
  blocks,
  designSystem,
  plugins,
  tenantDomain,
  apiVersion,
}) => {
  const renderBlock = useCallback(
    (block: Block, index: number) => {
      // Check isActive: can be number (1 = active) or boolean (false = inactive)
      const isBlockActive = block?.isActive !== false && block?.isActive !== 0;
      if (!isBlockActive || block?.properties?.enabled === false) {
        return null;
      }

      const TypeSafeBlockComponent = createTypeSafeBlockComponent(block);
      if (!TypeSafeBlockComponent) {
        return null;
      }

      return (
        <React.Fragment key={`header-${index}`}>
          <TypeSafeBlockComponent
            title={block.name}
            enabled={true}
            properties={block.properties as unknown}
            designSystem={designSystem}
            plugins={plugins}
            tenantDomain={tenantDomain}
            apiVersion={apiVersion}
          />
        </React.Fragment>
      );
    },
    [designSystem, plugins, tenantDomain, apiVersion]
  );

  if (!blocks || blocks.length === 0) {
    return null;
  }

  return <>{blocks.map((block, index) => renderBlock(block, index))}</>;
};

export default Header;
